/*
 * Axon Muscle Affiliate Program — API client + small helpers.
 *
 * This site lives at `affiliate.axonmuscle.com`. All routes are top-level
 * (no subdomain dispatcher), so `buildAffiliatePath('/foo')` simply returns
 * `/foo` and exists only as a stable indirection.
 *
 * The affiliate's own storefront link (e.g. `https://johndoe.axonmuscle.com`)
 * is built by `buildAffiliateUrl()` and shown on the dashboard.
 */

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND}/api`;
const LS_TOKEN = 'axon_aff_token';
const LS_ADMIN = 'axon_admin_token';

// ---------- Auth (affiliate session) ------------------------------------
export function getAuthToken() {
  if (typeof window === 'undefined') return null;
  try { return localStorage.getItem(LS_TOKEN); } catch { return null; }
}
export function setAuthToken(token) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(LS_TOKEN, token); } catch { /* ignore */ }
}
export function clearAuthToken() {
  if (typeof window === 'undefined') return;
  try { localStorage.removeItem(LS_TOKEN); } catch { /* ignore */ }
}

export async function affiliateApi(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const token = getAuthToken();
  if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;
  let res;
  try {
    res = await fetch(`${API}${path}`, { ...options, headers });
  } catch (networkErr) {
    const e = new Error('Network error. Please check your connection and try again.');
    e.cause = networkErr;
    throw e;
  }
  let data = null;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    const detail = (data && (data.detail || data.message)) || `Request failed (${res.status})`;
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  // Always return an object so callers can safely read properties.
  return data || {};
}

// ---------- Admin token storage -----------------------------------------
export const ADMIN_TOKEN_KEY = LS_ADMIN;
export function getAdminToken() {
  if (typeof window === 'undefined') return '';
  try { return localStorage.getItem(LS_ADMIN) || ''; } catch { return ''; }
}
export function setAdminToken(t) {
  try { localStorage.setItem(LS_ADMIN, t || ''); } catch { /* ignore */ }
}

// ---------- Path / URL helpers ------------------------------------------
/**
 * Affiliate-site internal navigation.
 * Always returns a top-level path because this app IS the affiliate site.
 */
export function buildAffiliatePath(path) {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * Build the affiliate's *storefront* link (the wildcard subdomain on the
 * parent storefront brand). Falls back to a same-origin `?aff=` URL in
 * preview/dev environments so the link is still clickable for QA.
 */
export function buildAffiliateUrl(subdomain) {
  if (!subdomain) return '';
  if (typeof window === 'undefined') return `https://${subdomain}.axonmuscle.com`;
  const host = window.location.hostname;
  // On affiliate.axonmuscle.com, the affiliate's storefront lives at
  // <name>.axonmuscle.com — strip the `affiliate.` prefix.
  if (host.endsWith('axonmuscle.com')) {
    return `https://${subdomain}.axonmuscle.com`;
  }
  // Preview / localhost — keep something clickable for testing.
  return `${window.location.origin}/?aff=${encodeURIComponent(subdomain)}`;
}
